if (!customElements.get('rs7gt-waves')) {
  customElements.define('rs7gt-waves', class extends HTMLElement {
    connectedCallback() {
      this.svg = this.querySelector('[data-waves-svg]');
      if (!this.svg) return;
      this.svg.pauseAnimations();
      this.started = false;
      this.visible = false;
      this.elapsed = 0;
      this.frame = 0;
      this.lastFrameTime = null;
      this.canvasUnavailable = false;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.launch = this.closest('rs7gt-launch');
      this.launch?.addEventListener('rs7gt:motionchange', () => this.updateMotion(), options);
      this.launch?.addEventListener('rs7gt:pagecoveragechange', () => this.updateMotion(), options);
      this.motionQuery.addEventListener('change', () => this.updateMotion(), options);
      document.addEventListener('visibilitychange', () => this.updateMotion(), options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio > 0.01;
        this.updateMotion();
      }, { threshold: [0, 0.01] });
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.stopFrame();
      this.svg?.pauseAnimations();
      this.canvas?.remove();
      this.canvas = this.context = null;
      this.removeAttribute('data-animating');
      this.removeAttribute('data-canvas');
    }

    prepareCanvas() {
      const paths = Array.from(this.svg.querySelectorAll('path > animate[attributeName="d"]'));
      const tone = this.svg.querySelector('animate[attributeName="fill"]');
      const lights = Array.from(this.svg.querySelectorAll('.rs7gt-waves__arc--upper .rs7gt-waves__light'));
      if (paths.length !== 2 || !tone || !lights.length) return false;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) {
        this.canvasUnavailable = true;
        return false;
      }
      this.pathFrames = paths.map(animation => animation.getAttribute('values').split(';').map(path => path.match(/-?\d+(?:\.\d+)?/g).map(Number)));
      this.tones = tone.getAttribute('values').split(';').map(color => [1, 3, 5].map(index => parseInt(color.slice(index, index + 2), 16)));
      this.keyTimes = tone.getAttribute('keyTimes').split(';').map(Number);
      this.splines = tone.getAttribute('keySplines').split(';').map(value => value.split(' ').map(Number));
      this.duration = parseFloat(tone.getAttribute('dur')) * 1000;
      let alpha = 0;
      this.layers = lights.map(light => {
        alpha += (1 - alpha) * Number(light.getAttribute('stroke-opacity'));
        return { width: Number(light.getAttribute('stroke-width')), alpha };
      });
      this.canvas = canvas;
      this.context = context;
      canvas.className = 'rs7gt-waves__canvas';
      canvas.setAttribute('aria-hidden', 'true');
      this.resizeCanvas();
      this.append(canvas);
      return true;
    }

    resizeCanvas() {
      if (!this.canvas) return;
      const scale = Math.min(1, 1200 / Math.max(this.clientWidth, this.clientHeight));
      const width = Math.max(1, Math.round(this.clientWidth * scale));
      const height = Math.max(1, Math.round(this.clientHeight * scale));
      if (this.canvas.width === width && this.canvas.height === height) return;
      this.canvas.width = width;
      this.canvas.height = height;
      this.drawCanvas();
    }

    splineProgress(value, spline) {
      if (spline[0] === spline[1] && spline[2] === spline[3]) return value;
      const cubic = (time, first, second) => 3 * (1 - time) ** 2 * time * first + 3 * (1 - time) * time ** 2 * second + time ** 3;
      let low = 0;
      let high = 1;
      for (let index = 0; index < 14; index++) {
        const time = (low + high) / 2;
        if (cubic(time, spline[0], spline[2]) < value) low = time;
        else high = time;
      }
      return cubic((low + high) / 2, spline[1], spline[3]);
    }

    drawCanvas() {
      const phase = (this.elapsed % this.duration) / this.duration;
      let index = 0;
      while (index < this.keyTimes.length - 2 && phase > this.keyTimes[index + 1]) index++;
      const progress = this.splineProgress((phase - this.keyTimes[index]) / (this.keyTimes[index + 1] - this.keyTimes[index]), this.splines[index]);
      const color = this.tones[index].map((value, channel) => value + (this.tones[index + 1][channel] - value) * progress);
      const tone = `rgb(${color.map(Math.round).join(',')})`;
      let previous = tone;
      const strokes = [];
      this.layers.forEach(layer => {
        const stroke = `rgb(${color.map(value => Math.round(value + (255 - value) * layer.alpha)).join(',')})`;
        if (stroke !== previous) strokes.push({ width: layer.width, color: stroke });
        previous = stroke;
      });
      const context = this.context;
      context.setTransform(this.canvas.width / 1904, 0, 0, this.canvas.height / 1088, 0, 0);
      context.fillStyle = tone;
      context.fillRect(0, 0, 1904, 1088);
      context.beginPath();
      this.pathFrames.forEach(frames => {
        const points = frames[index].map((value, coordinate) => value + (frames[index + 1][coordinate] - value) * progress);
        context.moveTo(points[0], points[1]);
        context.quadraticCurveTo(points[2], points[3], points[4], points[5]);
      });
      // Both arcs share increasingly bright, opaque tones. Drawing their union
      // per tone preserves the lighten result without a second canvas or copy.
      strokes.forEach(stroke => {
        context.lineWidth = stroke.width;
        context.strokeStyle = stroke.color;
        context.stroke();
      });
    }

    stopFrame() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.lastFrameTime = null;
    }

    tick(time) {
      this.frame = 0;
      if (!this.isConnected || !this.hasAttribute('data-animating') || document.hidden) return;
      if (this.lastFrameTime !== null) this.elapsed = (this.elapsed + time - this.lastFrameTime) % this.duration;
      this.lastFrameTime = time;
      this.drawCanvas();
      this.frame = requestAnimationFrame(next => this.tick(next));
    }

    updateMotion() {
      const active = this.isConnected && this.visible && !document.hidden
        && (this.launch?.sceneVisibility?.(this) ?? 1) > 0
        && this.dataset.designMode !== 'true' && !this.motionQuery.matches
        && !this.launch?.hasAttribute('data-motion-paused');
      this.toggleAttribute('data-animating', active);
      const useCanvas = !this.motionQuery.matches && this.dataset.designMode !== 'true' && !this.canvasUnavailable
        && (this.canvas || (active && this.prepareCanvas()));
      this.toggleAttribute('data-canvas', Boolean(useCanvas));
      if (this.canvas) this.canvas.hidden = !useCanvas;
      if (useCanvas) {
        this.svg.pauseAnimations();
        if (active && !this.frame) this.frame = requestAnimationFrame(time => this.tick(time));
        else if (!active) this.stopFrame();
        return;
      }
      this.stopFrame();
      if (active) {
        if (!this.started) {
          this.svg.querySelectorAll('animate').forEach(animation => animation.beginElement());
          this.started = true;
        }
        this.svg.unpauseAnimations();
      } else {
        this.svg.pauseAnimations();
      }
    }
  });
}
